import { isEqual } from 'lodash';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { OnboardingAdminRequests } from '@/services/requests';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { AccountManagerType, CorporateDocumentType } from '@/types/interfaces/Document.interface';
import { errorNotification, getCleanedPayload, successNotification } from '@/utils';

export const useOnboardingUserProfileStore = defineStore('OnboardingUserProfile', () => {
  const i18n = useI18n();

  const profile = ref<Onboarding.Business.CompanyDetails | undefined >();
  const userDetails = ref<Onboarding.Business.FullApplication['userDetails']>();
  const applicationStatus = ref<Onboarding.ApplicationStatus | undefined>('PENDING');
  const applicationId = ref();
  const businessActors = ref<Onboarding.Business.Actor[]>();
  const corporateDocs = ref();
  const isMailingAddressSame = ref(true);

  function formatActorData(actorData: Onboarding.Business.Actor[]): Onboarding.Business.Actor[] {
    return actorData.map((actor) => (actor.personalInfo === null
      ? { ...actor, personalInfo: {} } as Onboarding.Business.Actor : actor));
  }

  function setData(newData: Onboarding.Business.FullApplication) {
    profile.value = newData.companyDetailsDto ?? newData.companyDetails;
    userDetails.value = newData.userDetails;
    applicationStatus.value = newData?.applicationStatus;
    applicationId.value = newData?.id ?? applicationId.value;
    businessActors.value = formatActorData(newData?.businessActors);
    corporateDocs.value = newData?.corporateDocs;
    isMailingAddressSame.value = isEqual(profile.value?.mailingAddress, profile.value?.registeredAddress);
  }

  async function fetchProfile(id: string) {
    const response = await OnboardingAdminRequests.Business.getApplication(id);
    if (response.error === null) setData(response.response);
    return response;
  }

  async function updateCompanyDetails(newCompanyDetails: Onboarding.Business.CompanyDetails) {
    const response = await OnboardingAdminRequests.Business.updateCompanyDetails(applicationId.value, getCleanedPayload(newCompanyDetails));
    if (response.error === null) setData(response.response);
    return response;
  }

  async function uploadCorporateDocument(type: CorporateDocumentType, formData: FormData) {
    const response = await OnboardingAdminRequests.Business.uploadCorporateDocument({
      id: applicationId.value,
      type,
      formData,
    });

    if (response.error === null) {
      setData(response.response);
      successNotification(i18n.t('notification.file_successfully_uploaded'));
    } else {
      errorNotification(response.error);
    }
    return response;
  }

  async function uploadBusinessDocument(data: {type: AccountManagerType; formData: FormData; actorId: string}) {
    const response = await OnboardingAdminRequests.Business.uploadBusinessDocument({
      id: applicationId.value,
      type: data.type,
      formData: data.formData,
    }, data.actorId);

    if (response.error === null) {
      setData(response.response);
      successNotification(i18n.t('notification.file_successfully_uploaded'));
    } else {
      errorNotification(response.error);
    }
    return response;
  }

  async function addActor(data: Onboarding.Business.Actor) {
    const response = await OnboardingAdminRequests.Business.addBusinessActor(applicationId.value, data);
    if (response.error === null) { setData(response.response); }
    return response;
  }

  async function updateActor(data: Onboarding.Business.Actor, actorId: string) {
    const response = await OnboardingAdminRequests.Business.updateBusinessActor(applicationId.value, actorId, data);
    if (response.error === null) { setData(response.response); }
    return response;
  }

  async function deleteActor(actorId: string) {
    const response = await OnboardingAdminRequests.Business.deleteBusinessActor(applicationId.value, actorId);
    if (response.error === null) { setData(response.response); }
    return response;
  }

  async function approveApplication() {
    return OnboardingAdminRequests.Business.approveApplication(applicationId.value);
  }

  async function rejectApplication() {
    return OnboardingAdminRequests.Business.rejectApplication(applicationId.value);
  }

  function $reset() {
    delete profile.value;
    delete applicationStatus.value;
    delete applicationId.value;
    delete businessActors.value;
    delete corporateDocs.value;
  }

  return {
    profile,
    userDetails,
    applicationStatus,
    applicationId,
    businessActors,
    corporateDocs,
    isMailingAddressSame,

    fetchProfile,

    updateCompanyDetails,
    uploadCorporateDocument,
    uploadBusinessDocument,

    addActor,
    updateActor,
    deleteActor,

    approveApplication,
    rejectApplication,

    $reset,
  };
});
